<template>
  <div>
    <div class="d-flex flex-column gap-3">
      <!-- One layout on every page: the aggregates lead (they are what a sale is judged by), spread
           over the full width above the table. -->
      <SelectionSummaryComponent
        :available="{ mwh: filteredStats.totalMwh, count: filteredStats.count }"
        :selected="
          readonly
            ? null
            : {
                mwh: selection.totalSelectedMwh,
                count: selection.selectedItemIds.size,
              }
        "
      >
        <SelectionBreakdownsComponent
          :items="breakdownItems"
          :get-mwh="getItemMwh"
          :total="breakdownTotal"
        >
          <slot
            name="breakdowns"
            :items="breakdownItems"
            :get-mwh="getItemMwh"
            :total="breakdownTotal"
          />
        </SelectionBreakdownsComponent>
      </SelectionSummaryComponent>
      <div>
        <!-- Only the first load swaps the table out: a refetch must keep the table instance, which
             holds the quick filters the controls above show as active. -->
        <LoadingIndicatorComponent
          v-if="loading && items.length === 0"
          class="w-100"
        />
        <PortfolioItemsTable
          v-else
          ref="table"
          v-bind="tableOptions"
          :selection
          :items
          :disable-selection="readonly"
          :enable-count-editing="!readonly"
          :allow-multi-company-selection="true"
          table-size="compact"
          hide-action-bar
          @update:filtered-stats="onFilteredStatsChange"
        >
          <template v-if="!readonly" #header-start>
            <AutoSelectToolbarComponent
              v-model:enabled="autoSelectEnabled"
              v-model:target-mwh="targetMwh"
              v-model:spread="spreadOverCompanies"
              :spread-label="
                spreadToggle
                  ? $t('inventory_selection.spread_over_companies')
                  : undefined
              "
              :can-clear="hasSelection"
              @clear="selection.clearSelection()"
            >
              <select
                v-model="vintageYear"
                class="form-select form-select-sm w-auto"
                :aria-label="$t('inventory_selection.vintage')"
                data-testid="vintage-filter"
              >
                <option :value="null">
                  {{ $t("inventory_selection.vintage") }}
                </option>
                <option
                  v-for="year in availableVintageYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
              <button
                type="button"
                class="btn btn-sm"
                :class="standardAibGc ? 'btn-primary' : 'btn-outline-secondary'"
                data-testid="standard-aib-gc-filter"
                @click="toggleStandardAibGc"
              >
                {{ $t("inventory_selection.standard_aib_gc") }}
              </button>
              <button
                v-for="kind in TECH_QUICK_FILTER_KEYS"
                :key="kind"
                type="button"
                class="btn btn-sm"
                :class="
                  activeTechFilter === kind
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                "
                :data-testid="`tech-filter-${kind}`"
                @click="toggleTechFilter(kind)"
              >
                {{ $t(`inventory_selection.tech_filters.${kind}`) }}
              </button>
            </AutoSelectToolbarComponent>
          </template>
        </PortfolioItemsTable>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { sumBy, xor } from "lodash-es";
import { computed, ref, useTemplateRef, watch } from "vue";

import { STANDARD_AIB_GC_COUNTRIES } from "@/constants";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import type {
  FilterScalar,
  FilterState,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import type { PortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type { FilteredPortfolioStats, FlattenedPortfolioItem } from "@/types";
import {
  FORWARD_ELIGIBLE_TECHNOLOGIES,
  type EnergyTechnology,
} from "@/types/technology";

import { allocateAutoSelection } from "./autoSelectAllocation";
import AutoSelectToolbarComponent from "./AutoSelectToolbarComponent.vue";
import type {
  AutoSelectEligibility,
  InventoryTableOptions,
  ItemMwh,
} from "./inventorySelectionTypes";
import SelectionBreakdownsComponent from "./SelectionBreakdownsComponent.vue";
import SelectionSummaryComponent from "./SelectionSummaryComponent.vue";
import { useAutoSelect } from "./useAutoSelect";

type TechQuickFilter = "hsw" | "biomass" | "nuclear";
type StandardAibGcColumns = Pick<
  FilterState,
  "issuing_region" | "registration_authority"
>;

const TECH_QUICK_FILTERS: Record<TechQuickFilter, EnergyTechnology[]> = {
  hsw: [...FORWARD_ELIGIBLE_TECHNOLOGIES],
  biomass: ["biomass"],
  nuclear: ["nuclear"],
};
const TECH_QUICK_FILTER_KEYS = Object.keys(
  TECH_QUICK_FILTERS
) as TechQuickFilter[];
const STANDARD_AIB_GC_FILTERS: StandardAibGcColumns = {
  issuing_region: [...STANDARD_AIB_GC_COUNTRIES],
  registration_authority: ["AIB"],
};

/**
 * The certificate-selection workspace shared by every page that picks inventory to sell or buy:
 * quick filters, the certificate table with auto-select, and a breakdowns column summarising the
 * selection (or, with nothing selected, the filtered rows).
 *
 * Auto-select works on the rows the table currently shows: selections outside the active filters
 * are kept, those inside are replaced by the pick. It re-runs when its target, the spread choice,
 * the rows or `autoSelectEligible` changes, so a page makes eligibility depend on e.g. its price by passing a
 * new function.
 *
 * `readonly` shows a fixed selection (a saved transaction's volumes): no filters, no auto-select,
 * no selecting.
 *
 * The controls sit in the table's header: auto-select and Clear, then the quick filters (vintage,
 * Standard AIB GC, technology), so filtering and selecting read as one row. A quick filter is a
 * shortcut onto the table's own column filters and shows as active exactly while those columns
 * hold its values, so clearing a column in the table turns its quick filter off too.
 *
 * Slot: `breakdowns` (extra cards between the production months and the support breakdown, given
 * the rows, each row's MWh and their total).
 */
const props = withDefaults(
  defineProps<{
    items: FlattenedPortfolioItem[];
    selection: PortfolioSelection;
    tableOptions: InventoryTableOptions;
    loading?: boolean;
    readonly?: boolean;
    /** Show a checkbox for `v-model:spread`; without it auto-select spreads as the page set it. */
    spreadToggle?: boolean;
    autoSelectEligible?: AutoSelectEligibility;
  }>(),
  {
    loading: false,
    readonly: false,
    spreadToggle: false,
    autoSelectEligible: () => true,
  }
);

/** Whether auto-select spreads over companies rather than filling greedily; spreads by default. */
const spreadOverCompanies = defineModel<boolean>("spread", { default: true });

const tableRef =
  useTemplateRef<InstanceType<typeof PortfolioItemsTable>>("table");

const filteredStats = ref({ count: 0, totalMwh: 0 });
const filteredItems = ref<FlattenedPortfolioItem[]>([]);

// A refetch (e.g. a new price listing other rows) changes what the target is filled from, so it
// re-runs too.
const { enabled: autoSelectEnabled, targetMwh } = useAutoSelect({
  run: runAutoSelect,
  sources: [
    spreadOverCompanies,
    () => props.autoSelectEligible,
    () => props.items,
  ],
});

const hasSelection = computed(() => props.selection.selectedItemIds.size > 0);

const breakdownItems = computed(() =>
  hasSelection.value ? props.selection.selectedItems : filteredItems.value
);

const getItemMwh: ItemMwh = (item) =>
  props.selection.getSelectedCount(item.id) ?? item.count;

const breakdownTotal = computed(() => sumBy(breakdownItems.value, getItemMwh));

const availableVintageYears = computed(() =>
  availableYearsOf(listedMonths.value)
);

const listedMonths = computed((): string[] =>
  (tableRef.value?.getFilterOptions().production_month ?? []).map((m) =>
    String(m.value)
  )
);

const monthsOfYear = (year: number, months = listedMonths.value): string[] =>
  months.filter((month) => month.startsWith(`${year}-`));

// The quick filters read their state off the table's filters rather than keeping their own copy.
const vintageYear = computed({
  get: (): number | null =>
    availableVintageYears.value.find((year) =>
      holdsExactly(
        tableRef.value?.filterValues.production_month,
        monthsOfYear(year)
      )
    ) ?? null,
  set: (year: number | null) =>
    tableRef.value?.setColumnFilter(
      "production_month",
      year === null ? [] : monthsOfYear(year)
    ),
});

const activeTechFilter = computed(
  (): TechQuickFilter | null =>
    TECH_QUICK_FILTER_KEYS.find((kind) =>
      holdsExactly(
        tableRef.value?.filterValues.technology_type,
        TECH_QUICK_FILTERS[kind]
      )
    ) ?? null
);

const standardAibGc = computed(() => {
  const values = tableRef.value?.filterValues;
  return (
    values !== undefined &&
    holdsExactly(
      values.issuing_region,
      STANDARD_AIB_GC_FILTERS.issuing_region
    ) &&
    holdsExactly(
      values.registration_authority,
      STANDARD_AIB_GC_FILTERS.registration_authority
    )
  );
});

// A vintage quick filter holds the year's months as listed when it was picked; a refetch that lists
// another month of that year would leave it matching no year (the dropdown blank, the old months
// still filtering), so it follows the year's months as listed now.
watch(listedMonths, (months, previous) => {
  const filtered = tableRef.value?.filterValues.production_month;
  const year = availableYearsOf(previous ?? []).find((y) =>
    holdsExactly(filtered, monthsOfYear(y, previous ?? []))
  );
  if (year === undefined) return;
  const current = monthsOfYear(year, months);
  if (current.length > 0 && !holdsExactly(filtered, current))
    tableRef.value?.setColumnFilter("production_month", current);
});

function runAutoSelect(targetMwh: number) {
  const visibleItems = tableRef.value?.getFilteredItems() ?? [];
  const visibleIds = new Set(visibleItems.map((item) => item.id));
  for (const id of Array.from(props.selection.selectedItemIds)) {
    if (visibleIds.has(id)) props.selection.deselectItem(id);
  }
  const allocations = allocateAutoSelection(
    visibleItems.filter(props.autoSelectEligible),
    targetMwh,
    { spreadOverCompanies: spreadOverCompanies.value }
  );
  for (const { id, count } of allocations) {
    props.selection.updateItemSelectedCount(id, count);
  }
}

function onFilteredStatsChange(stats: FilteredPortfolioStats) {
  filteredStats.value = stats;
  filteredItems.value = stats.items;
}

// Standard AIB GC: AIB certificates issued in the countries buyers accept as one standard product.
// It takes over the issuing-region and scheme columns while on; turning it off gives back whatever
// the buyer had chosen in those columns before.
let filtersBeforeStandardAibGc: StandardAibGcColumns | null = null;

function toggleStandardAibGc() {
  const table = tableRef.value;
  if (!table) return;
  if (standardAibGc.value) {
    table.applyColumnFilters(
      filtersBeforeStandardAibGc ?? {
        issuing_region: [],
        registration_authority: [],
      }
    );
    filtersBeforeStandardAibGc = null;
  } else {
    filtersBeforeStandardAibGc = {
      issuing_region: [...table.filterValues.issuing_region],
      registration_authority: [...table.filterValues.registration_authority],
    };
    table.applyColumnFilters(STANDARD_AIB_GC_FILTERS);
  }
}

function toggleTechFilter(kind: TechQuickFilter) {
  tableRef.value?.setColumnFilter(
    "technology_type",
    activeTechFilter.value === kind ? [] : TECH_QUICK_FILTERS[kind]
  );
}

/** The production years among `months` ("YYYY-MM"), newest first. */
function availableYearsOf(months: readonly string[]): number[] {
  const years = new Set(months.map((month) => parseInt(month.split("-")[0])));
  return [...years].filter((year) => !isNaN(year)).sort((a, b) => b - a);
}

/** Whether a list column's filter holds exactly `values`, in any order. */
function holdsExactly(
  current: readonly FilterScalar[] | undefined,
  values: readonly FilterScalar[]
): boolean {
  return (
    current !== undefined &&
    values.length > 0 &&
    current.length === values.length &&
    xor(current, values).length === 0
  );
}
</script>
